import { useRef, useState, useMemo, useEffect, useContext } from "react";
import { X, Search, Filter, Upload, ImagePlus, LoaderCircle, CheckCircle2 } from "lucide-react";
import axios from "axios";
import styles from "./CreativeStudioGallery.module.css";
import { BASE_URL_API } from '../../../References/URLs';
import AuthContext from '../../../store/auth-context';
import { toast } from "react-toastify";

const cleanFileName = (name) => {
    return name
        .toLowerCase()
        .replace(/[^a-z0-9.\-_]/g, "-")
        .replace(/-+/g, "-");
};

export default function CreativeStudioGallery({
    isOpen,
    onClose,
    onSelectImage,
    storeImages = [],
    vendorIdentifier,
    storeIdentifier,
    authIdentifier,
    onStoreImagesUploaded,
}) {
    const authCtx = useContext(AuthContext);
    const [activeTab, setActiveTab] = useState("stock"); // "stock" or "store"
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedFilter, setSelectedFilter] = useState(null);
    const [hoveredId, setHoveredId] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const [pendingUploads, setPendingUploads] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadedStoreImages, setUploadedStoreImages] = useState([]);
    const [stockImages, setStockImages] = useState([]);
    const fileInputRef = useRef(null);
    const [isFetchingStoreImages, setIsFetchingStoreImages] = useState(false);
    const [isFetchingStockImages, setIsFetchingStockImages] = useState(false);

    // Fetch stock images from API
    const getStockImages = async () => {
        if (!vendorIdentifier || !storeIdentifier) return;

        setIsFetchingStockImages(true);
        try {
            const response = await fetch(BASE_URL_API + "getStockImages", {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx?.token,
                    vendorIdentifier,
                    storeIdentifier,
                }),
            });

            if (!response.ok) {
                console.log("Something went wrong: Server Error");
                setStockImages([]);
                return;
            }

            const respo = await response.json();
            if (respo.status === "success" && Array.isArray(respo.response)) {
                setStockImages(respo.response);
            } else {
                setStockImages([]);
            }
        } catch (err) {
            console.error("Error fetching stock images:", err);
            setStockImages([]);
        } finally {
            setIsFetchingStockImages(false);
        }
    };

    // Fetch store images from API
    const getStoreImages = async () => {
        if (!vendorIdentifier || !storeIdentifier) return;
        
        setIsFetchingStoreImages(true);
        try {
            const response = await fetch(BASE_URL_API + "getStoreImages", {
                method: "POST",
                body: JSON.stringify({
                    token : authCtx?.token,
                    vendorIdentifier,
                    storeIdentifier,
                }),
            });

            if (!response.ok) {
                console.log("Something went wrong: Server Error");
                setIsFetchingStoreImages(false);
                return;
            }

            const respo = await response.json();
            if (respo.status === "success" && Array.isArray(respo.response)) {
                // Map API response to normalized format
                const mappedImages = respo.response.map((item, idx) => ({
                    id: item.uuid || `store-api-${idx}`,
                    url: item.cdn_url,
                    title: `Store Image ${idx + 1}`,
                    tags: ["store"],
                    uuid: item.uuid,
                    created_at: item.created_at,
                    uploaded_by: item.uploaded_by,
                }));
                setUploadedStoreImages(mappedImages);
            } else {
                setUploadedStoreImages([]);
            }
        } catch (err) {
            console.error("Error fetching store images:", err);
            setUploadedStoreImages([]);
        } finally {
            setIsFetchingStoreImages(false);
        }
    };

    // Fetch store and stock images when component opens or identifiers change
    useEffect(() => {
        if (isOpen && vendorIdentifier && storeIdentifier) {
            getStockImages();
            getStoreImages();
        }
    }, [isOpen, vendorIdentifier, storeIdentifier]);

    // Get all unique tags from fetched stock images
    const allTags = useMemo(() => {
        const tags = new Set();
        stockImages.forEach(img => {
            JSON.parse(img.tags || "[]").forEach(tag => tags.add(tag));
        });
        return Array.from(tags).sort();
    }, [stockImages]);

    const normalizedStoreImages = useMemo(() => {
        const mappedExisting = (storeImages || []).map((item, idx) => {
            if (typeof item === "string") {
                return {
                    id: `store-existing-${idx}`,
                    url: item,
                    title: `Store Image ${idx + 1}`,
                    tags: ["store"],
                };
            }
            return {
                id: item.id || `store-existing-${idx}`,
                url: item.url || item.cloudFrontURL,
                title: item.title || `Store Image ${idx + 1}`,
                tags: item.tags || ["store"],
            };
        }).filter((item) => !!item.url);

        return [...uploadedStoreImages, ...mappedExisting];
    }, [storeImages, uploadedStoreImages]);

    // Filter images based on search and filter
    const filteredImages = useMemo(() => {
        const images = activeTab === "stock" ? stockImages : normalizedStoreImages;
        return images.filter(img => {
            const matchesSearch = !searchQuery || 
                (img.title || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
                (img.tags || []).some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
            
            const matchesFilter = !selectedFilter || (img.tags || []).includes(selectedFilter);
            
            return matchesSearch && matchesFilter;
        });
    }, [activeTab, searchQuery, selectedFilter, normalizedStoreImages]);

    const handleFileSelection = (fileList) => {
        const files = Array.from(fileList || []).filter((file) => file.type.startsWith("image/"));
        if (!files.length) return;

        const nextItems = files.map((file) => ({
            id: `${Date.now()}-${file.name}-${Math.random().toString(36).slice(2, 7)}`,
            file,
            preview: URL.createObjectURL(file),
            progress: 0,
            cloudFrontURL: "",
            status: "pending",
            error: null,
        }));

        setPendingUploads((prev) => [...prev, ...nextItems]);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        handleFileSelection(e.dataTransfer.files);
    };

    const removePending = (id) => {
        setPendingUploads((prev) => {
            const current = prev.find((item) => item.id === id);
            if (current?.preview) URL.revokeObjectURL(current.preview);
            return prev.filter((item) => item.id !== id);
        });
    };

    const uploadFile = async (item) => {
        const cleanedName = cleanFileName(item.file.name);
        const key = `uploads/${storeIdentifier}/${Date.now()}-${cleanedName}`;

        const { data } = await axios.get("https://bucket-api.lokaci.com/gpu-lokaci-pro-creative-studio", {
            params: {
                fileName: key,
                contentType: item.file.type,
                vendorIdentifier,
                storeIdentifier,
                authIdentifier,
                fileSizeKb: Number((item.file.size / 1024).toFixed(2)),
            },
        });

        const uploadURL = data?.uploadURL;
        const cloudFrontURL = data?.cloudFrontURL || data?.fileURL || "";

        if (!uploadURL) throw new Error("Upload URL missing from upload API response");

        await axios.put(uploadURL, item.file, {
            headers: { "Content-Type": item.file.type },
            onUploadProgress: (progressEvent) => {
                const total = progressEvent.total || item.file.size;
                const percent = Math.round((progressEvent.loaded * 100) / total);
                setPendingUploads((prev) =>
                    prev.map((entry) =>
                        entry.id === item.id ? { ...entry, progress: percent, status: "uploading" } : entry
                    )
                );
            },
        });

        const finalUrl = cloudFrontURL || uploadURL.split("?")[0];

        setPendingUploads((prev) =>
            prev.map((entry) =>
                entry.id === item.id
                    ? { ...entry, progress: 100, cloudFrontURL: finalUrl, status: "done", error: null }
                    : entry
            )
        );

        return {
            id: `store-uploaded-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            url: finalUrl,
            title: item.file.name,
            tags: ["store"],
        };
    };

    const handleUploadAll = async () => {
        if (!vendorIdentifier || !storeIdentifier || !authIdentifier) return;
        const queue = pendingUploads.filter((item) => item.status !== "done");
        if (!queue.length) return;

        setIsUploading(true);
        const successful = [];

        for (const item of queue) {
            try {
                const uploaded = await uploadFile(item);
                successful.push(uploaded);
            } catch (err) {
                setPendingUploads((prev) =>
                    prev.map((entry) =>
                        entry.id === item.id
                            ? { ...entry, status: "error", error: err?.message || "Upload failed" }
                            : entry
                    )
                );
            }
        }

        if (successful.length) {
            setUploadedStoreImages((prev) => [...successful, ...prev]);
            if (typeof onStoreImagesUploaded === "function") {
                onStoreImagesUploaded(successful);
            }
        }

        setIsUploading(false);
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Overlay */}
            <div 
                className={styles.overlay}
                onClick={onClose}
            />

            {/* Slider Panel */}
            <div className={styles.sliderPanel}>
                {/* Header */}
                <div className={styles.header}>
                    <div className={styles.headerTop}>
                        <h2 className={styles.title}>Choose Image</h2>
                        <button 
                            className={styles.closeBtn}
                            onClick={onClose}
                            title="Close gallery"
                        >
                            <X size={18} strokeWidth={2.5} />
                        </button>
                    </div>

                    {/* Tabs */}
                    <div className={styles.tabs}>
                        <button
                            className={`${styles.tab} ${activeTab === "stock" ? styles.activeTab : ""}`}
                            onClick={() => setActiveTab("stock")}
                        >
                            Stock Images {isFetchingStockImages ? <LoaderCircle size={12} className={styles.spin} style={{ marginLeft: "4px" }} /> : `(${stockImages.length})`}
                        </button>
                        <button
                            className={`${styles.tab} ${activeTab === "store" ? styles.activeTab : ""}`}
                            onClick={() => setActiveTab("store")}
                        >
                            Store Images {isFetchingStoreImages ? <LoaderCircle size={12} className={styles.spin} style={{ marginLeft: "4px" }} /> : `(${normalizedStoreImages.length})`}
                        </button>
                    </div>

                    {/* Search Bar */}
                    <div className={styles.searchBox}>
                        <Search size={15} strokeWidth={2} className={styles.searchIcon} />
                        <input
                            type="text"
                            placeholder="Search images..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            className={styles.searchInput}
                        />
                        {searchQuery && (
                            <button
                                className={styles.clearBtn}
                                onClick={() => setSearchQuery("")}
                            >
                                <X size={14} strokeWidth={2} />
                            </button>
                        )}
                    </div>
                </div>

                {/* Filters */}
                {activeTab === "stock" && (
                    <div className={styles.filterContainer}>
                        <div className={styles.filterLabel}>
                            <Filter size={13} strokeWidth={2.5} />
                            Filters
                        </div>
                        <div className={styles.filterTags}>
                            <button
                                className={`${styles.filterTag} ${!selectedFilter ? styles.activeFilter : ""}`}
                                onClick={() => setSelectedFilter(null)}
                            >
                                All
                            </button>
                            {allTags.map(tag => (
                                <button
                                    key={tag}
                                    className={`${styles.filterTag} ${selectedFilter === tag ? styles.activeFilter : ""}`}
                                    onClick={() => setSelectedFilter(tag)}
                                >
                                    {tag}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {activeTab === "store" && (
                    <div className={styles.storeUploadSection}>
                        <div
                            className={`${styles.dropZone} ${dragOver ? styles.dropZoneActive : ""}`}
                            onDragOver={(e) => {
                                e.preventDefault();
                                setDragOver(true);
                            }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                multiple
                                className={styles.hiddenInput}
                                onChange={(e) => handleFileSelection(e.target.files)}
                            />
                            <ImagePlus size={16} strokeWidth={2.2} />
                            <span>Drag & drop images or click to browse</span>
                        </div>

                        {pendingUploads.length > 0 && (
                            <div className={styles.pendingList}>
                                {pendingUploads.map((item) => (
                                    <div key={item.id} className={styles.pendingItem}>
                                        <img src={item.preview} alt={item.file.name} className={styles.pendingThumb} />
                                        <div className={styles.pendingMeta}>
                                            <div className={styles.pendingName}>{item.file.name}</div>
                                            <div className={styles.pendingSub}>
                                                {(item.file.size / 1024).toFixed(1)} KB
                                                {item.status === "uploading" ? ` • ${item.progress}%` : ""}
                                                {item.status === "done" ? " • Uploaded" : ""}
                                                {item.status === "error" ? ` • ${item.error}` : ""}
                                            </div>
                                        </div>
                                        {item.status === "done" ? (
                                            <CheckCircle2 size={16} className={styles.doneIcon} />
                                        ) : (
                                            <button
                                                className={styles.removeBtn}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removePending(item.id);
                                                }}
                                                title="Remove"
                                            >
                                                <X size={14} />
                                            </button>
                                        )}
                                    </div>
                                ))}

                                <button
                                    className={styles.finalUploadBtn}
                                    onClick={handleUploadAll}
                                    disabled={
                                        isUploading ||
                                        !pendingUploads.some((item) => item.status !== "done") ||
                                        !vendorIdentifier ||
                                        !storeIdentifier ||
                                        !authIdentifier
                                    }
                                >
                                    {isUploading ? <LoaderCircle size={14} className={styles.spin} /> : <Upload size={14} />}
                                    {isUploading ? "Uploading..." : "Upload Selected Images"}
                                </button>

                                {(!vendorIdentifier || !storeIdentifier || !authIdentifier) && (
                                    <div className={styles.uploadHint}>Missing store/auth context. Please refresh and try again.</div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Gallery Grid */}
                <div className={styles.galleryContainer}>
                    {filteredImages.length > 0 ? (
                        <div className={styles.gallery}>
                            {filteredImages.map(img => (
                                <div
                                    key={img.id}
                                    className={styles.imageCard}
                                    onMouseEnter={() => setHoveredId(img.id)}
                                    onMouseLeave={() => setHoveredId(null)}
                                >
                                    <img
                                        src={img.url}
                                        alt={img.title}
                                        className={styles.imageThumbnail}
                                        loading="lazy"
                                    />
                                    {hoveredId === img.id && (
                                        <div className={styles.imageOverlay}>
                                            <button
                                                className={styles.selectBtn}
                                                onClick={() => {
                                                    onSelectImage(img.url);
                                                    onClose();
                                                }}
                                                title={`Select ${img.title}`}
                                            >
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="20 6 9 17 4 12"></polyline>
                                                </svg>
                                            </button>
                                        </div>
                                    )}
                                    <div className={styles.imageTitle}>{img.title}</div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className={styles.emptyState}>
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                <polyline points="21 15 16 10 5 21"></polyline>
                            </svg>
                            <p>No images found</p>
                            <span>Try adjusting your search or filters</span>
                        </div>
                    )}
                </div>

                {/* Footer Info */}
                <div className={styles.footer}>
                    <span className={styles.resultCount}>
                        {filteredImages.length} image{filteredImages.length !== 1 ? "s" : ""}
                    </span>
                </div>
            </div>
        </>
    );
}
